'use client';
import ColoredTag from '@/components/shared/colored-tag/colored-tag.component';
import Typography from '@/components/shared/typography/typography.component';
import { useFilter, useTranslation } from '@/hook';
import { ClearAllTag, FilterGroup } from '@/components/filtersV2';
import React, { memo } from 'react';
import colors from '@/theme/colors.module.scss';
import {
    CarStatus,
    CarStatusText,
    defaultEmiFilter,
    defaultPriceFilter,
    defaultYearFilter,
    FilterDisplayItemData,
    IClientRetailFilters,
    ISearchTerms,
    KILOMETERS,
    MakeFilterUiOject,
    TRANSMISSION,
    YEAR_FILTER_DATA,
} from '@/types';
import { AVAILABLE_LANGUAGES } from '@/constants';
import { numberWithCommas } from '@/utils';
import {
    formatBrandName,
    formatPriceForDisplay,
    formatYearForDisplay,
    slugify,
} from '@/utils/filterUtil';
import { CarTypesEnum } from '../car-details-v2/car-type';
import {
    getCategoryBadgeConfig,
    CATEGORY_BADGE_CONFIG,
} from '../car-details/CarProfileNew/constants';

interface TFiltersProps {
    filters: IClientRetailFilters;
    setFilters: (arg: IClientRetailFilters) => void;
    searchTerms: ISearchTerms;
    displayedCount: number;
    lang: AVAILABLE_LANGUAGES;
}
const Filters = memo(
    ({
        filters,
        setFilters,
        searchTerms,
        displayedCount,
        lang,
    }: TFiltersProps) => {
        const { t } = useTranslation();
        const {
            makeModelValueChanged,
            kilometersValueChanged,
            bodyTypeValueChanged,
            transmissionValueChanged,
            priceRangeChanged,
            emiRangeChanged,
            yearRangeChanged,
            availableValueChanged,
            availableTagClicked,
            kilometersTagClicked,
            transmissionTagClicked,
            bodyTypeTagClicked,
            yearTagClicked,
            priceTagClicked,
            emiTagClicked,
            makeTagClicked,
            modelTagClicked,
            clearFilters,
            filterObjectNotEmpty,
            localFilters,
            discountedValueToggle,
            discountedTagClicked,
            categoryValuedChanged,
            categoryTagClicked,
        } = useFilter(searchTerms, filters, setFilters);

        const badgeInfo =
            filters.category !== 'all' &&
            getCategoryBadgeConfig(filters.category || CarTypesEnum.LITE, t);

        const handleCategoryFilterClick = (value: string | undefined) => {
            if (value === undefined || value === 'undefined') {
                categoryValuedChanged('all');
            } else {
                categoryValuedChanged(value as CarTypesEnum);
            }
        };
        return (
            <>
                <Typography
                    style={{ color: colors.gray700 }}
                    variant={'body2SemiBold'}
                >
                    {t('Filters')}
                </Typography>
                <div
                    style={{
                        marginBottom: '12px',
                        ...(filterObjectNotEmpty
                            ? { paddingBottom: '16px', marginBottom: '16px' }
                            : {}),
                    }}
                >
                    {Object.keys(localFilters.makeModel).map(make => {
                        return Object.keys(localFilters.makeModel[make].models)
                            .length ===
                            searchTerms.makeModelTree[make].modelsSlug
                                .length ? (
                            <ColoredTag
                                key={make}
                                text={`${formatBrandName(
                                    searchTerms.makeModelTree[make].make
                                )} - ${t('any')}`}
                                tagClicked={makeTagClicked}
                            />
                        ) : (
                            Object.keys(
                                localFilters.makeModel[make].models
                            ).map(model => (
                                <ColoredTag
                                    key={model}
                                    text={`${formatBrandName(
                                        searchTerms.makeModelTree[make].make
                                    )} - ${formatBrandName(
                                        searchTerms.makeModelTree[make].models[
                                            model
                                        ]
                                    )}`}
                                    tagClicked={() =>
                                        modelTagClicked({
                                            make,
                                            model,
                                        })
                                    }
                                />
                            ))
                        );
                    })}

                    {Object.keys(localFilters.bodystyle).length !== 0 &&
                        Object.keys(localFilters.bodystyle).map(item => {
                            return (
                                <ColoredTag
                                    key={item}
                                    text={
                                        searchTerms.bodyStyleTerms.bodyStyles[
                                            item
                                        ]
                                    }
                                    tagClicked={bodyTypeTagClicked}
                                />
                            );
                        })}

                    {Object.keys(localFilters.transmission).length !== 0 &&
                        Object.keys(localFilters.transmission).map(item => (
                            <ColoredTag
                                key={item}
                                text={item}
                                tagClicked={transmissionTagClicked}
                            />
                        ))}

                    {localFilters.kilometers &&
                        localFilters.kilometers !== 0 && (
                            <ColoredTag
                                text={`${numberWithCommas(
                                    localFilters.kilometers
                                )} ${t('km')} ${t('& Less')}`}
                                tagClicked={kilometersTagClicked}
                            />
                        )}

                    {localFilters.status && (
                        <ColoredTag
                            text={t(CarStatusText[localFilters.status]) || ''}
                            tagClicked={availableTagClicked}
                        />
                    )}

                    {localFilters.year.min != defaultYearFilter.min && (
                        <ColoredTag
                            text={formatYearForDisplay(
                                localFilters.year.max !== defaultYearFilter.max
                                    ? [
                                          localFilters.year.min,
                                          localFilters.year.max,
                                      ]
                                    : [localFilters.year.min]
                            )}
                            tagClicked={yearTagClicked}
                        />
                    )}

                    {localFilters.price.min !== defaultPriceFilter.min && (
                        <ColoredTag
                            text={formatPriceForDisplay(
                                [
                                    Math.max(
                                        localFilters.price.min,
                                        searchTerms.priceTerms.min
                                    ),
                                    Math.min(
                                        localFilters.price.max,
                                        searchTerms.priceTerms.max
                                    ),
                                ],
                                lang,
                                t
                            )}
                            tagClicked={priceTagClicked}
                        />
                    )}

                    {localFilters.discounted && (
                        <ColoredTag
                            text={'Discounted'}
                            tagClicked={discountedTagClicked}
                        />
                    )}

                    {filters.category !== undefined &&
                        filters.category !== 'all' && (
                            <ColoredTag
                                text={badgeInfo.title}
                                tagClicked={categoryTagClicked}
                            />
                        )}

                    {localFilters.emi.min !== defaultEmiFilter.min && (
                        <ColoredTag
                            text={formatPriceForDisplay(
                                [
                                    Math.max(
                                        localFilters.emi.min,
                                        searchTerms.emiTerms.min
                                    ),
                                    Math.min(
                                        localFilters.emi.max,
                                        searchTerms.emiTerms.max
                                    ),
                                ],
                                lang,
                                t
                            )}
                            tagClicked={emiTagClicked}
                        />
                    )}
                </div>

                {filterObjectNotEmpty && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography
                            style={{
                                color: colors.gray400,
                            }}
                            variant={'captionMedium'}
                        >{`${displayedCount} ${t('Cars Found')}`}</Typography>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        ></div>
                        <ClearAllTag
                            tagClicked={clearFilters}
                            variant={'red'}
                        />
                    </div>
                )}

                <FilterGroup
                    key={`price-${localFilters.price.min}-${localFilters.price.max}`}
                    label={t('price')}
                    data={{
                        min: searchTerms.priceTerms.min,
                        max: searchTerms.priceTerms.max,
                    }}
                    range={[
                        Math.max(
                            localFilters.price.min,
                            searchTerms.priceTerms.min
                        ),
                        Math.min(
                            localFilters.price.max,
                            searchTerms.priceTerms.max
                        ),
                    ]}
                    filterType="slider"
                    rangeChanged={priceRangeChanged}
                    formatDisplayedValue={v => {
                        const formatedNumber = numberWithCommas(v);
                        return `${t('EGP')} ${formatedNumber}`;
                    }}
                    vertical={false}
                />
                <label className="relative flex justify-between items-center text-xl mb-4">
                    <Typography
                        className={'text-gray-700'}
                        variant={'body2Medium'}
                    >
                        {t('discountedFilterLabel')}
                    </Typography>
                    <input
                        type="checkbox"
                        onChange={discountedValueToggle}
                        checked={localFilters.discounted}
                        className="absolute left-1/2 -translate-x-1/2 w-full h-full peer appearance-none rounded-md"
                    />
                    <span
                        className="w-10 h-6 flex items-center flex-shrink-0 ml-2 p-0.5 bg-gray-300 rounded-full duration-300
                    ease-in-out peer-checked:bg-primary
                    after:w-5 after:h-5
                    after:bg-white after:rounded-full
                    after:shadow-md after:duration-300
                    en:peer-checked:after:translate-x-4 ar:peer-checked:after:-translate-x-4"
                    ></span>
                </label>
                <FilterGroup
                    label={t('Refurbished Status')}
                    data={[
                        {
                            text: t('All Cars'),
                            value: undefined,
                            description: t('AllCarsDesc'),
                            selected:
                                filters.category === 'all' ||
                                filters.category === undefined,
                        },
                        ...searchTerms.categoryTerms.categoryRaw
                            .sort((a, b) => {
                                const order = [
                                    CarTypesEnum.CERTIFIED,
                                    CarTypesEnum.LITE,
                                    CarTypesEnum.Dealer,
                                ];
                                return order.indexOf(a) - order.indexOf(b);
                            })
                            .map(cat => ({
                                text: t(CATEGORY_BADGE_CONFIG[cat].title),
                                icon: CATEGORY_BADGE_CONFIG[cat].filterIcon,
                                description: t(CATEGORY_BADGE_CONFIG[cat].desc),
                                selected: filters.category === cat,
                                value: cat,
                                isNew: CATEGORY_BADGE_CONFIG[cat].isNew,
                            })),
                    ]}
                    filterType="radiobox"
                    filterItemClicked={handleCategoryFilterClick}
                />
                <FilterGroup
                    containerExtraCssClass={' h-[286px] '}
                    label={t('Make & Model')}
                    data={Object.keys(searchTerms.makeModelTree).map(make => {
                        return {
                            text: formatBrandName(make),
                            value: make,
                            models: Object.keys(
                                searchTerms.makeModelTree[make].models
                            ).map(
                                model =>
                                    ({
                                        text: searchTerms.makeModelTree[make]
                                            .models?.[model],
                                        value: model,
                                        selected: !!(
                                            localFilters.makeModel[make]
                                                ?.models?.[model] || false
                                        ),
                                    } as FilterDisplayItemData)
                            ),
                            selected: !!localFilters.makeModel[make],
                        } as MakeFilterUiOject;
                    })}
                    filterType="multiSelect"
                    filterItemClicked={() => null}
                    makeModelClicked={makeModelValueChanged}
                />

                <FilterGroup
                    key={`emi-${localFilters.emi.min}-${localFilters.emi.max}`}
                    label={t('emi')}
                    data={{
                        min: searchTerms.emiTerms.min,
                        max: searchTerms.emiTerms.max,
                    }}
                    range={[
                        Math.max(
                            localFilters.emi.min,
                            searchTerms.emiTerms.min
                        ),
                        Math.min(
                            localFilters.emi.max,
                            searchTerms.emiTerms.max
                        ),
                    ]}
                    filterType="slider"
                    formatDisplayedValue={v => {
                        const formatedNumber = numberWithCommas(v);
                        return `${t('EGP')} ${formatedNumber}`;
                    }}
                    rangeChanged={emiRangeChanged}
                    vertical={false}
                />

                <FilterGroup
                    label={t('Car Status')}
                    data={Object.values(CarStatus).map(cs => {
                        return {
                            text: t(CarStatusText[cs]),
                            value: cs,
                            selected:
                                localFilters.status &&
                                slugify(localFilters.status) === slugify(cs),
                        } as FilterDisplayItemData;
                    })}
                    filterType="radiobox"
                    filterItemClicked={availableValueChanged}
                />
                <FilterGroup
                    label={t('Year')}
                    data={YEAR_FILTER_DATA}
                    filterType="fromTo"
                    rangeChanged={yearRangeChanged}
                    range={
                        localFilters.year.min != defaultYearFilter.min
                            ? localFilters.year.max != defaultYearFilter.max
                                ? [localFilters.year.min, localFilters.year.max]
                                : [localFilters.year.min]
                            : [0, 0]
                    }
                />
                <FilterGroup
                    containerExtraCssClass={'h-auto '}
                    label={t('kilometers')}
                    data={Object.values(KILOMETERS).map(km => {
                        return {
                            text: `${numberWithCommas(km)} ${t('KM & Less')}`,
                            value: km,
                            selected: localFilters.kilometers === Number(km),
                        } as FilterDisplayItemData;
                    })}
                    filterType="radiobox"
                    filterItemClicked={kilometersValueChanged}
                />
                <FilterGroup
                    label={t('Body Style')}
                    data={searchTerms.bodyStyleTerms.bodyStylesRaw.map(bt => {
                        return {
                            text: bt,
                            value: bt,
                            selected: !!localFilters.bodystyle[bt],
                        } as FilterDisplayItemData;
                    })}
                    filterType="checkbox"
                    filterItemClicked={bodyTypeValueChanged}
                />
                <FilterGroup
                    label={t('Transmission')}
                    data={Object.values(TRANSMISSION).map(t => {
                        return {
                            text: t,
                            value: t,
                            selected: !!localFilters.transmission[t],
                        } as FilterDisplayItemData;
                    })}
                    filterType="checkbox"
                    filterItemClicked={transmissionValueChanged}
                />
            </>
        );
    }
);
Filters.displayName = 'Filters';
export default Filters;
